import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  Video,
  ExternalLink,
  Eye,
  EyeOff,
  Search,
  CheckCircle2,
  Film
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';

// Cache for fetched Vimeo thumbnails to prevent redundant network requests
const thumbCache = new Map();

// Lightweight Vimeo Thumbnail Component (Fetches only image, no heavy iframe)
function VimeoThumb({ url, title }) {
  const [thumbUrl, setThumbUrl] = useState(() => (url ? thumbCache.get(url) : null));

  useEffect(() => {
    if (!url || thumbCache.has(url)) return;
    let isMounted = true;

    // Vimeo oEmbed public API returns thumbnail_url
    const oembedEndpoint = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}&width=240`;

    fetch(oembedEndpoint)
      .then(res => res.json())
      .then(data => {
        if (isMounted && data?.thumbnail_url) {
          thumbCache.set(url, data.thumbnail_url);
          setThumbUrl(data.thumbnail_url);
        }
      })
      .catch(() => {
        // Fallback or unlisted video
      });

    return () => {
      isMounted = false;
    };
  }, [url]);

  if (thumbUrl) {
    return (
      <img
        src={thumbUrl}
        alt={title || 'Vimeo thumbnail'}
        className="w-16 h-12 object-cover rounded-lg border border-slate-200 shadow-xs bg-slate-100"
        loading="lazy"
      />
    );
  }

  return (
    <div className="w-16 h-12 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-500 shadow-xs">
      <Video className="w-5 h-5 text-blue-600" />
    </div>
  );
}

const AdminTestimonials = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterFeatured, setFilterFeatured] = useState('all'); // 'all' | 'featured' | 'hidden'

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    vimeo_url: '',
    is_featured: true,
    order_position: 1,
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('video_testimonials')
        .select('*')
        .order('order_position', { ascending: true });

      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: isFr ? 'Erreur de chargement' : 'Error loading videos',
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    const nextOrder = testimonials.length > 0
      ? Math.max(...testimonials.map(t => t.order_position || 0)) + 1
      : 1;

    setEditingItem(null);
    setFormData({
      title: '',
      vimeo_url: '',
      is_featured: true,
      order_position: nextOrder,
    });
    setIsEditing(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      vimeo_url: item.vimeo_url || '',
      is_featured: item.is_featured ?? true,
      order_position: item.order_position ?? 0,
    });
    setIsEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vimeo_url.trim()) {
      toast({
        variant: 'destructive',
        title: isFr ? 'URL requise' : 'URL required',
        description: isFr ? 'Veuillez saisir une URL Vimeo valide' : 'Please provide a valid Vimeo URL'
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title.trim() || null,
        vimeo_url: formData.vimeo_url.trim(),
        is_featured: Boolean(formData.is_featured),
        order_position: parseInt(formData.order_position) || 0,
      };

      if (editingItem) {
        const { error } = await supabase
          .from('video_testimonials')
          .update(payload)
          .eq('id', editingItem.id);

        if (error) throw error;
        toast({
          title: isFr ? 'Témoignage mis à jour' : 'Testimonial updated',
          description: isFr ? 'Le témoignage vidéo a été mis à jour.' : 'Video testimonial was updated successfully.'
        });
      } else {
        const { error } = await supabase
          .from('video_testimonials')
          .insert([payload]);

        if (error) throw error;
        toast({
          title: isFr ? 'Témoignage ajouté' : 'Testimonial created',
          description: isFr ? 'La nouvelle vidéo a été enregistrée avec succès.' : 'New video testimonial was added successfully.'
        });
      }

      setIsEditing(false);
      fetchTestimonials();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: isFr ? 'Erreur' : 'Error',
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleFeatured = async (item) => {
    try {
      const newStatus = !item.is_featured;
      const { error } = await supabase
        .from('video_testimonials')
        .update({ is_featured: newStatus })
        .eq('id', item.id);

      if (error) throw error;

      setTestimonials(prev => prev.map(t => t.id === item.id ? { ...t, is_featured: newStatus } : t));
      toast({
        title: newStatus
          ? (isFr ? 'Vidéo mise en avant' : 'Video featured')
          : (isFr ? 'Vidéo masquée' : 'Video hidden'),
        description: isFr
          ? (newStatus ? 'La vidéo est maintenant active sur le site.' : 'La vidéo est maintenant masquée du site.')
          : (newStatus ? 'The video is now active on the website.' : 'The video is now hidden from the website.')
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: isFr ? 'Erreur' : 'Error',
        description: error.message
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isFr ? 'Êtes-vous sûr de vouloir supprimer cette vidéo témoignage ?' : 'Are you sure you want to delete this video testimonial?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('video_testimonials')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: isFr ? 'Vidéo supprimée' : 'Video deleted',
        description: isFr ? 'Le témoignage a été supprimé.' : 'The video testimonial was removed.'
      });
      fetchTestimonials();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: isFr ? 'Erreur' : 'Error',
        description: error.message
      });
    }
  };

  // Filtered list
  const filteredList = testimonials.filter(item => {
    const matchesSearch =
      (item.title && item.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.vimeo_url && item.vimeo_url.toLowerCase().includes(searchQuery.toLowerCase()));

    if (!matchesSearch) return false;

    if (filterFeatured === 'featured') return item.is_featured === true;
    if (filterFeatured === 'hidden') return item.is_featured === false;
    return true;
  });

  return (
    <AdminLayout>
      <Helmet>
        <title>{isFr ? 'Témoignages Vidéo' : 'Video Testimonials'} - Admin - Bigadsy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="p-2 rounded-xl bg-blue-50 text-blue-600 border border-blue-100">
                <Video className="h-5 w-5" />
              </span>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                {isFr ? 'Témoignages Vidéo' : 'Video Testimonials'}
              </h1>
            </div>
            <p className="text-sm text-slate-500">
              {isFr
                ? 'Gérez la liste des vidéos Vimeo de témoignages affichées sur le site'
                : 'Manage Vimeo testimonial videos displayed in the website carousel'}
            </p>
          </div>

          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] hover:opacity-95 text-white shadow-sm shadow-blue-500/20 font-semibold rounded-xl h-11 px-5 self-start sm:self-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            {isFr ? 'Ajouter une vidéo' : 'Add Testimonial Video'}
          </Button>
        </div>

        {/* Clean, Fast Create / Edit Form Modal */}
        <AnimatePresence>
          {isEditing && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden"
            >
              <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Video className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      {editingItem
                        ? (isFr ? 'Modifier la vidéo' : 'Edit Testimonial Video')
                        : (isFr ? 'Ajouter une vidéo Vimeo' : 'Add New Vimeo Video')}
                    </h2>
                    <p className="text-xs text-slate-500">
                      {isFr ? 'Entrez l\'URL Vimeo et la position dans le carrousel' : 'Enter the Vimeo link and order position'}
                    </p>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsEditing(false)}
                  className="rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    {isFr ? 'Titre / Référence Client' : 'Video Title / Client Reference'}
                  </label>
                  <input
                    type="text"
                    placeholder={isFr ? 'Ex: Témoignage Clinic Smile - Dr. Karim' : 'e.g. Testimonial 1 - Client Name'}
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-medium"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {isFr ? 'Titre interne pour identifier la vidéo' : 'Internal label to identify the video'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    {isFr ? 'URL de la vidéo Vimeo' : 'Vimeo Video URL'} <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="https://vimeo.com/1193209010"
                    value={formData.vimeo_url}
                    onChange={(e) => setFormData({ ...formData, vimeo_url: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Format: https://vimeo.com/123456789 ou lien de lecteur embed
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                      {isFr ? 'Position d\'affichage (Order)' : 'Order Position'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.order_position}
                      onChange={(e) => setFormData({ ...formData, order_position: e.target.value })}
                      className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-semibold"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      {isFr ? 'Ordre dans le carrousel (1, 2, 3...)' : 'Carousel sort position (1, 2, 3...)'}
                    </p>
                  </div>

                  <div className="flex flex-col justify-center pt-2">
                    <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100/70 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-semibold text-slate-800 block">
                          {isFr ? 'En ligne (is_featured)' : 'Featured on Website'}
                        </span>
                        <span className="text-xs text-slate-500 block">
                          {isFr ? 'Visible sur la page d\'accueil' : 'Visible in public carousel'}
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                    className="border-slate-200 text-slate-700 hover:bg-slate-100 rounded-xl px-5"
                  >
                    {isFr ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] hover:opacity-90 text-white font-semibold rounded-xl px-6 shadow-sm shadow-blue-500/20"
                  >
                    {submitting
                      ? (isFr ? 'Enregistrement...' : 'Saving...')
                      : (editingItem ? (isFr ? 'Mettre à jour' : 'Update Video') : (isFr ? 'Enregistrer' : 'Save Video'))}
                  </Button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filter and Search Bar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="h-4 w-4 absolute left-3.5 top-3 text-slate-400" />
            <input
              type="text"
              placeholder={isFr ? 'Rechercher par titre ou URL...' : 'Search by title or URL...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="inline-flex rounded-xl p-1 bg-slate-100 border border-slate-200 w-full sm:w-auto text-xs font-semibold">
              <button
                onClick={() => setFilterFeatured('all')}
                className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition-all ${filterFeatured === 'all'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                  }`}
              >
                {isFr ? 'Tous' : 'All'} ({testimonials.length})
              </button>
              <button
                onClick={() => setFilterFeatured('featured')}
                className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition-all ${filterFeatured === 'featured'
                    ? 'bg-white text-emerald-700 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                  }`}
              >
                {isFr ? 'En ligne' : 'Featured'} ({testimonials.filter(t => t.is_featured).length})
              </button>
              <button
                onClick={() => setFilterFeatured('hidden')}
                className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition-all ${filterFeatured === 'hidden'
                    ? 'bg-white text-slate-700 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                  }`}
              >
                {isFr ? 'Masqués' : 'Hidden'} ({testimonials.filter(t => !t.is_featured).length})
              </button>
            </div>
          </div>
        </div>

        {/* Testimonials List Table (Thumb Only, order_position, is_featured) */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-3"></div>
            <p className="text-sm">{isFr ? 'Chargement des témoignages...' : 'Loading video list...'}</p>
          </div>
        ) : filteredList.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center">
            <div className="h-14 w-14 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center mx-auto mb-4">
              <Film className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-1">
              {searchQuery ? (isFr ? 'Aucun résultat' : 'No videos found') : (isFr ? 'Aucun témoignage vidéo' : 'No video testimonials yet')}
            </h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
              {searchQuery
                ? (isFr ? 'Essayez de modifier votre terme de recherche.' : 'Try adjusting your search or filter.')
                : (isFr ? 'Ajoutez vos vidéos Vimeo pour alimenter le carrousel.' : 'Add your Vimeo URLs to feed the testimonial carousel.')}
            </p>
            {!searchQuery && (
              <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
                <Plus className="mr-2 h-4 w-4" />
                {isFr ? 'Ajouter une première vidéo' : 'Add First Video'}
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200/90 rounded-2xl shadow-xs overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-xs font-bold uppercase tracking-wider border-b border-slate-200">
                  <th className="p-4 sm:p-5 w-24">Aperçu</th>
                  <th className="p-4 sm:p-5">Titre & URL Vimeo</th>
                  <th className="p-4 sm:p-5">Position</th>
                  <th className="p-4 sm:p-5">En vedette</th>
                  <th className="p-4 sm:p-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredList.map((item) => (
                  <tr key={item.id} className="text-slate-800 hover:bg-slate-50/70 transition-colors">
                    {/* Thumb Only */}
                    <td className="p-4 sm:p-5">
                      <VimeoThumb url={item.vimeo_url} title={item.title} />
                    </td>

                    {/* Title and Vimeo Link */}
                    <td className="p-4 sm:p-5">
                      <div className="font-bold text-slate-900 text-sm">
                        {item.title || 'Vidéo sans titre'}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="font-mono text-xs text-slate-500 truncate max-w-[280px]">
                          {item.vimeo_url}
                        </span>
                        <a
                          href={item.vimeo_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-700 p-0.5"
                          title="Ouvrir le lien Vimeo"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </td>

                    {/* Order Position */}
                    <td className="p-4 sm:p-5">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-slate-100 text-slate-800 text-xs font-bold border border-slate-200">
                        #{item.order_position}
                      </span>
                    </td>

                    {/* is_featured Toggle */}
                    <td className="p-4 sm:p-5">
                      <button
                        type="button"
                        onClick={() => handleToggleFeatured(item)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer ${item.is_featured
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                            : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                          }`}
                        title="Cliquer pour changer le statut"
                      >
                        {item.is_featured ? (
                          <>
                            <Eye className="h-3.5 w-3.5" />
                            En vedette (Actif)
                          </>
                        ) : (
                          <>
                            <EyeOff className="h-3.5 w-3.5" />
                            Masqué (Inactif)
                          </>
                        )}
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="p-4 sm:p-5 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(item)}
                          className="h-9 w-9 text-blue-600 hover:bg-blue-50 rounded-xl"
                          title="Modifier"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(item.id)}
                          className="h-9 w-9 text-rose-600 hover:bg-rose-50 rounded-xl"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTestimonials;
